import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { authenticateFirestoreClient, defaultAuth, loginVerifierAuth } from "../../firebase";
import { validateActiveAccount } from "../../authStatus";
import LoginErrorMessage from "../common/LoginErrorMessage";
import { assertRoleLoginAllowed, useRoleSessionState } from "../../auth/roleSession";

const db = getFirestore(app);

function getFriendlyAuthError(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/user-disabled":
      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Try again later or reset the password.";
    case "auth/network-request-failed":
    case "unavailable":
    case "deadline-exceeded":
      return "Network error. Check your connection and try again.";
    case "auth/role-already-active":
      return error.message;
    default:
      return "Unable to sign in. Please try again.";
  }
}

export default function RegistarLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loading: sessionLoading, user: sessionUser } = useRoleSessionState("Registrar");

  useEffect(() => {
    if (!sessionLoading && sessionUser) navigate("/registar/dashboard", { replace: true });
  }, [navigate, sessionLoading, sessionUser]);

  if (sessionLoading) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    let persistentAuth = null;
    let createdPersistentSession = false;
    let loginSucceeded = false;
    let firestoreAuthUid = null;
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const verifiedCredential = await signInWithEmailAndPassword(loginVerifierAuth, normalizedEmail, password);
      const uid = verifiedCredential.user.uid;
      await authenticateFirestoreClient(normalizedEmail, password, uid);
      firestoreAuthUid = uid;
      const verifiedProfile = await getDoc(doc(db, "users", uid));
      if (!verifiedProfile.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const verifiedData = verifiedProfile.data();
      const validation = validateActiveAccount(verifiedData, "Account");
      if (!validation.active) {
        setError(validation.message);
        return;
      }
      if (verifiedData.role !== "Registrar") {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        return;
      }

      persistentAuth = await assertRoleLoginAllowed("Registrar", uid);
      if (persistentAuth.currentUser?.uid !== uid) {
        const persistentCredential = await signInWithEmailAndPassword(persistentAuth, normalizedEmail, password);
        createdPersistentSession = true;
        if (persistentCredential.user.uid !== uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
          setError("Unable to sign in. Please try again.");
          return;
        }
      }

      const userDoc = await getDoc(doc(db, "users", uid));
      if (!userDoc.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const persistentData = userDoc.data();
      const persistentValidation = validateActiveAccount(persistentData, "Account");
      if (!persistentValidation.active) {
        setError(persistentValidation.message);
        if (persistentAuth.currentUser?.uid === uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }

      if (persistentData.role === "Registrar") {
        navigate("/registar/dashboard");
      } else {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        if (createdPersistentSession) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }
      loginSucceeded = true;
    } catch (err) {
      setError(err?.code === "auth/role-already-active" ? err.message : getFriendlyAuthError(err));
    } finally {
      try {
        if (!loginSucceeded && firestoreAuthUid && defaultAuth.currentUser?.uid === firestoreAuthUid) {
          await signOut(defaultAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Firestore login session", cleanupError);
      }
      try {
        if (createdPersistentSession && !loginSucceeded && persistentAuth) {
          await signOut(persistentAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Registrar login session", cleanupError);
      }
      try {
        await signOut(loginVerifierAuth);
      } catch (cleanupError) {
        console.error("Failed to clean up login verifier session", cleanupError);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)",
      padding: 20,
      boxSizing: "border-box"
    }}>
      <div style={{ width: 420, maxWidth: "100%", background: "#fff", padding: 28, borderRadius: 20, boxShadow: "0 6px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#155c2b", letterSpacing: 1 }}>EnrollEase</div>
          <div style={{ fontSize: 13, color: "#666", marginTop: 8 }}>Registrar Portal</div>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Email</label>
          <input
            type="email"
            placeholder="you@school.edu.ph"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={{ width: "100%", marginBottom: 12, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
            autoComplete="username"
          />

          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Password</label>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              style={{ width: "100%", padding: "12px 40px 12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
              autoComplete="current-password"
            />
            <span
              onClick={() => setShowPassword(s => !s)}
              style={{ position: "absolute", right: 12, top: 12, color: "#888", cursor: "pointer", userSelect: "none" }}
            >
              {showPassword ? "Hide" : "Show"}
            </span>
          </div>

          <LoginErrorMessage message={error} />
          <div style={{ textAlign: "right", marginBottom: 12 }}>
            <Link to="/forgot-password?scope=Registrar&returnTo=%2Fregistar-login" style={{ color: "#155c2b", fontSize: 14 }}>Forgot Password?</Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ width: "100%", padding: "12px 0", background: loading ? "#ccc" : "#155c2b", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 16, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Signing in..." : "LOG IN"}
          </button>
        </form>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Link to="/" style={{ color: "#155c2b", fontSize: 14 }}>Return to home</Link>
          </div>
      </div>
    </div>
  );
}
